import { Checkbox, Form, Input, Select } from "antd";
import queryClient from "api/queryClient.ts";
import { useGetAccountGlobalFormFieldsQuery, useGetMeQuery, useUpdateMeMutation } from "api/User/hooks.ts";
import { AxiosError } from "axios";
import { FormCol } from "components/UI/Form/FormCol";
import { FormGrid } from "components/UI/Form/FormGrid";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { TenantCategorySelectSearchable } from "components/UI/Selects/TenantCategorySelectSearchable/TenantCategorySelectSearchable";
import { TenantWebTypeSelectSearchable } from "components/UI/Selects/TenantWebTypeSelectSearchable/TenantWebTypeSelectSearchable";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, ReactNode, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { FormFieldTypes } from "types/enums.ts";
import { ApiErrorResponse, FormId } from "types/models";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";

import { AccountProfileFormData, schema } from "./formSchema";

import UpdateMeRequest = App.Http.Requests.User.UpdateMeRequest;

export const AccountProfileFormSettingsContent: FC<{ email: string | null }> = ({ email }) => {
    const { t } = usePaldockTranslation("forms.account-profile");
    const { control, fieldNames } = useGeneralForm<AccountProfileFormData>();

    return (
        <FormCol>
            <FormGrid cols={2}>
                <Form.Item label={t("fields.email.label")} className="!mb-0">
                    <Input disabled value={email ?? ""} />
                </Form.Item>
                <FormItem control={control} name={fieldNames.nickname.name} label={t("fields.nickname.label")}>
                    <Input />
                </FormItem>
            </FormGrid>
        </FormCol>
    );
};

const AccountProfileFormSync: FC<{ defaultValues: AccountProfileFormData }> = ({ defaultValues }) => {
    const { reset } = useGeneralForm<AccountProfileFormData>();

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    return null;
};

export const AccountProfileFormAffiliateContent: FC = () => {
    const { t } = usePaldockTranslation("forms.affiliate-propagation");
    const { control, fieldNames } = useGeneralForm<AccountProfileFormData>();
    const showInPartnerCatalog = useWatch({ control, name: "show_in_partner_catalog" });

    return (
        <FormCol>
            <FormGrid cols={1}>
                <FormItem control={control} name={fieldNames.show_in_partner_catalog.name} valuePropName="checked">
                    <Checkbox>{t("fields.show_in_partner_catalog.label")}</Checkbox>
                </FormItem>
            </FormGrid>
            <FormGrid cols={1}>
                <FormItem
                    control={control}
                    name={fieldNames.categories.name}
                    label={t("fields.categories.label")}
                    required={showInPartnerCatalog}
                >
                    <TenantCategorySelectSearchable mode="multiple" />
                </FormItem>
            </FormGrid>
            <FormGrid cols={1}>
                <FormItem control={control} name={fieldNames.websites.name} label={t("fields.websites.label")}>
                    <Select mode="tags" tokenSeparators={[","]} placeholder={t("fields.websites.placeholder")} />
                </FormItem>
            </FormGrid>
            <FormGrid cols={1}>
                <FormItem control={control} name={fieldNames.web_types.name} label={t("fields.web_types.label")}>
                    <TenantWebTypeSelectSearchable mode="multiple" />
                </FormItem>
            </FormGrid>
            <FormGrid cols={1}>
                <FormItem
                    control={control}
                    name={fieldNames.affiliate_propagation_info.name}
                    label={t("fields.affiliate_propagation_info.label")}
                >
                    <Input.TextArea rows={4} />
                </FormItem>
            </FormGrid>
        </FormCol>
    );
};

const emptyGlobalFieldValue = (field: App.Data.GlobalFormFieldData): unknown => {
    if (field.type === FormFieldTypes.Checkbox) {
        return false;
    }
    if (field.type === FormFieldTypes.Number || field.type === FormFieldTypes.Country) {
        return null;
    }

    return "";
};

export const AccountProfileForm: FC<{ children: ReactNode }> = ({ children }) => {
    const { t } = usePaldockTranslation("forms.account-profile");

    const meRequest = { filter: { include: "catalogCategories,websites,catalogWebTypes" } };
    const getMeQuery = useGetMeQuery(meRequest);
    const accountGlobalFieldsQuery = useGetAccountGlobalFormFieldsQuery();
    const me = getMeQuery.data?.user;

    const globalFormFieldValues = useMemo(
        () =>
            Object.fromEntries(
                (accountGlobalFieldsQuery.data?.globalFormFields ?? []).map(({ field, value }) => [
                    field.system_name,
                    value ?? field.default_value ?? emptyGlobalFieldValue(field),
                ])
            ),
        [accountGlobalFieldsQuery.data?.globalFormFields]
    );

    const updateMeMutation = useUpdateMeMutation({
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["me"] });
            await queryClient.invalidateQueries({ queryKey: ["accountGlobalFormFields"] });
            await queryClient.invalidateQueries({ queryKey: ["getPartners"] });
            toast.success(t("messages.update_success"));
        },
    });

    const defaultValues = useMemo<AccountProfileFormData>(
        () => ({
            nickname: me?.nickname ?? "",
            show_in_partner_catalog: me?.show_in_partner_catalog ?? false,
            categories: me?.catalogCategories?.map((c) => c.category) ?? [],
            websites: me?.websites?.map((w) => w.url) ?? [],
            web_types: me?.catalogWebTypes?.map((w) => w.web_type) ?? [],
            affiliate_propagation_info: me?.affiliate_propagation_info ?? "",
            global_form_field_values: globalFormFieldValues,
        }),
        [globalFormFieldValues, me]
    );

    const onSubmit = async (data: AccountProfileFormData) => {
        try {
            const payload: UpdateMeRequest = {
                show_in_partner_catalog: data.show_in_partner_catalog,
                affiliate_propagation_info: data.affiliate_propagation_info || null,
                catalog_categories: data.categories,
                catalog_web_types: data.web_types,
                websites: data.websites,
            };
            if (data.nickname !== (me?.nickname ?? "")) {
                payload.nickname = data.nickname;
            }
            const changedGlobalValues = Object.fromEntries(
                Object.entries(data.global_form_field_values).filter(
                    ([systemName, value]) => JSON.stringify(value) !== JSON.stringify(globalFormFieldValues[systemName])
                )
            );
            if (Object.keys(changedGlobalValues).length > 0) {
                payload.global_form_field_values = changedGlobalValues;
            }
            await updateMeMutation.mutateAsync(payload);
        } catch (error) {
            const errorFields = Object.keys(
                (error as AxiosError<ApiErrorResponse>).response?.data?.exception?.errors ?? {}
            );
            const isInvalidWebsiteError = errorFields.some((field) => field.startsWith("websites."));

            toast.error(
                isInvalidWebsiteError
                    ? t("messages.invalid_website_url")
                    : (extractValidationErrorMessages(error as AxiosError<ApiErrorResponse>)[0] ??
                          t("messages.update_error"))
            );
        }
    };

    return (
        <GeneralFormProvider<AccountProfileFormData>
            key={me?.id ?? "me"}
            id={FormId.AccountProfileForm}
            schema={schema}
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            resetOnSubmit={false}
        >
            <AccountProfileFormSync defaultValues={defaultValues} />
            {children}
        </GeneralFormProvider>
    );
};
